import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { BarChart3 } from "lucide-react";
import { api } from "../lib/api";

const pad = (n) => String(n).padStart(2, "0");
const today = () => { const d = new Date(); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; };
const T = [["normal", "Normal", "#16a34a"], ["coupe", "Coupé", "#2563eb"], ["iddsi6", "IDDSI 6", "#ea580c"], ["iddsi5", "IDDSI 5", "#7c3aed"], ["iddsi4", "IDDSI 4", "#a21caf"]];

export default function StatsRepas() {
  const { t } = useTranslation();
  const [st, setSt] = useState(null);
  const [date, setDate] = useState(today());
  const [service, setService] = useState("midi");
  const load = useCallback(async () => {
    try { const { data } = await api.get("/cuisine/repas/state", { params: { date, service } }); setSt(data); } catch { }
  }, [date, service]);
  useEffect(() => { load(); }, [load]);
  if (!st) return <div className="p-8 text-muted-foreground">…</div>;
  const rooms = (st.rooms || []).filter((r) => r.status === "present");
  const n = rooms.length || 1;
  const cnt = (fn) => rooms.filter(fn).length;
  const tex = T.map(([k, l, c]) => [l, cnt((r) => r.prefs.texture === k), c]);
  const adapted = rooms.length - cnt((r) => r.prefs.texture === "normal");
  const liq = ["0", "1", "2", "3", "4"].map((x) => [`IDDSI ${x}`, cnt((r) => r.prefs.liquides === x), "#0284c7"]);
  const allerg = cnt((r) => (r.prefs.allergies || []).length);
  const excl = {};
  rooms.forEach((r) => (r.prefs.exclusions || []).forEach((e) => { excl[e] = (excl[e] || 0) + 1; }));
  const Bar = ({ label, val, col }) => (
    <div className="flex items-center gap-3">
      <span className="w-24 text-xs font-bold uppercase text-muted-foreground">{label}</span>
      <div className="flex-1 h-5 rounded-full bg-white/10 overflow-hidden"><div className="h-5 rounded-full flex items-center justify-end pr-2 text-[10px] font-extrabold text-white transition-all" style={{ width: `${Math.max(6, (val / n) * 100)}%`, background: col }}>{val}</div></div>
      <span className="w-12 text-right text-xs font-bold">{Math.round((val / n) * 100)} %</span>
    </div>
  );
  return (
    <div className="space-y-5 p-4 sm:p-6" data-testid="stats-repas">
      <div className="rounded-3xl p-6 sm:p-8 text-white relative overflow-hidden" style={{ background: "linear-gradient(135deg,#042f2e,#0d9488 60%,#2dd4bf)" }}>
        <div className="text-xs font-semibold tracking-[0.2em] uppercase text-white/60 flex items-center gap-1.5"><BarChart3 className="h-3.5 w-3.5" /> {st.maison}</div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold mt-1">{t("nav.statsRepas")}</h1>
        <p className="text-white/70 text-sm mt-1">{t("stats.subtitle")}</p>
        <div className="flex items-center gap-2 mt-4 flex-wrap">
          <input type="date" data-testid="stats-date" value={date} onChange={(e) => setDate(e.target.value)} className="h-9 rounded-full border border-white/30 bg-white/10 text-white px-3 text-sm font-semibold" />
          {[["midi", "☀️"], ["soir", "🌙"]].map(([s, ic]) => (
            <button key={s} data-testid={`stats-${s}`} onClick={() => setService(s)} className={`h-9 px-4 rounded-full text-sm font-bold border transition ${service === s ? "bg-white text-teal-800 border-white" : "border-white/30 text-white hover:bg-white/10"}`}>{ic} {s === "midi" ? t("rp.midi") : t("rp.soir")}</button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[[rooms.length, t("stats.residents"), "#0d9488"], [adapted, t("stats.adapted"), "#7c3aed"], [allerg, t("rp.allergies"), "#dc2626"], [cnt((r) => r.prefs.regime), t("rp.regime"), "#16a34a"]].map(([v, l, c]) => (
          <div key={l} className="rounded-2xl bg-white text-slate-800 p-4 text-center shadow">
            <div className="text-4xl font-extrabold" style={{ color: c }}>{v}</div><div className="text-[11px] font-bold uppercase text-slate-400 mt-1">{l}</div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-2xl glass p-5 space-y-2" data-testid="stats-textures">
          <h2 className="font-display font-bold text-teal-400 uppercase text-sm mb-3">🍽 {t("rp.textureSolide")} (IDDSI)</h2>
          {tex.map(([l, v, c]) => <Bar key={l} label={l} val={v} col={c} />)}
        </div>
        <div className="rounded-2xl glass p-5 space-y-2">
          <h2 className="font-display font-bold text-sky-400 uppercase text-sm mb-3">💧 {t("rp.liquides")} (IDDSI 0-4)</h2>
          {liq.map(([l, v, c]) => <Bar key={l} label={l} val={v} col={c} />)}
        </div>
        <div className="rounded-2xl glass p-5 space-y-2">
          <h2 className="font-display font-bold text-orange-400 uppercase text-sm mb-3">🚫 {t("rp.exclusions")}</h2>
          {Object.entries(excl).map(([l, v]) => <Bar key={l} label={l} val={v} col="#ea580c" />)}
          {!Object.keys(excl).length && <p className="text-xs text-muted-foreground">—</p>}
        </div>
        <div className="rounded-2xl glass p-5 space-y-2">
          <h2 className="font-display font-bold text-red-400 uppercase text-sm mb-3">⚠️ {t("rp.allergies")}</h2>
          {rooms.filter((r) => (r.prefs.allergies || []).length).map((r) => (
            <div key={r.room} className="rounded-xl bg-red-500/10 ring-1 ring-red-500 px-3 py-2 text-sm font-bold text-red-400">Ch. {r.room} — {(r.prefs.allergies || []).join(", ").toUpperCase()}</div>
          ))}
          {allerg === 0 && <p className="text-xs text-muted-foreground">—</p>}
        </div>
      </div>
      <p className="text-xs text-muted-foreground">{t("stats.audit")}</p>
    </div>
  );
}
